import React from 'react';
import { Box, Divider, Typography } from '@mui/material';
import { AgeBracket, ChecklistData, Region } from '../checklistData';
import { CheckedMap, StageStats } from '../checklistUtils';
import { colors, contentBlockShadow } from '../../../../theme';
import StageStepper from './StageStepper';
import ChecklistTaskRow from './ChecklistTaskRow';

interface TrainingPlanProps {
  data: ChecklistData;
  stats: StageStats[];
  checked: CheckedMap;
  expanded: CheckedMap;
  onCheck: (taskId: string) => void;
  onExpand: (taskId: string) => void;
  ageBracket: AgeBracket;
  region: Region;
  registerTaskRef: (taskId: string, el: HTMLDivElement | null) => void;
}

function TrainingPlan({
  data, stats, checked, expanded, onCheck, onExpand, ageBracket, region, registerTaskRef,
}: TrainingPlanProps) {
  return (
    <Box
      sx={{
        bgcolor: '#fff',
        borderRadius: '12px',
        boxShadow: contentBlockShadow,
        p: { xs: '20px 16px', md: '26px 30px' },
      }}
    >
      <Typography
        sx={{
          textAlign: 'center', fontWeight: 700, fontSize: { xs: 18, md: 20 }, lineHeight: 1.25,
        }}
      >
        30-Day Checklist
      </Typography>
      <Box sx={{ mt: { xs: 2.5, md: 3.25 }, mb: { xs: 0.5, md: 0.75 } }}>
        <StageStepper stages={data.stages} stats={stats} />
      </Box>
      {data.stages.map((stage, i) => {
        const s = stats[i];
        const complete = s.done === s.total;
        const pct = s.total === 0 ? 0 : Math.round((s.done / s.total) * 100);
        const titleColor = complete ? colors.primary.main : 'text.primary';
        const countColor = complete ? colors.primary.main : 'text.secondary';
        return (
          <Box
            key={stage.id}
            sx={{
              // No divider above the header — the accordion top/bottom borders below
              // are the only lines inside a period block (see design spec).
              pt: { xs: 2, md: 2.5 },
              mt: { xs: 2, md: 2.75 },
            }}
          >
            <Box
              sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.25, flexWrap: 'wrap',
              }}
            >
              <Typography sx={{
                fontWeight: 700, fontSize: { xs: 15, md: 16 }, color: titleColor, minWidth: 0,
              }}
              >
                {`${stage.range}: ${stage.name}`}
              </Typography>
              <Typography sx={{
                fontWeight: 600, fontSize: 13, color: countColor, whiteSpace: 'nowrap',
              }}
              >
                {`${s.done} of ${s.total} completed • ${pct}%`}
              </Typography>
            </Box>
            <Box sx={{
              height: 5, borderRadius: '3px', bgcolor: colors.primary[50], mt: 1.25, overflow: 'hidden',
            }}
            >
              <Box sx={{
                height: '100%', width: `${pct}%`, borderRadius: '3px', bgcolor: colors.primary.main, transition: 'width 400ms ease',
              }}
              />
            </Box>
            <Typography sx={{
              fontSize: 13, color: 'text.secondary', mt: 1.25, lineHeight: 1.45,
            }}
            >
              {stage.desc}
            </Typography>
            <Box
              sx={{
                mt: 1.25,
                // The accordions carry the only lines in the block: a top border below
                // the progress bar and a bottom border closing the last item.
                borderTop: `1px solid ${colors.grey.divider}`,
                borderBottom: `1px solid ${colors.grey.divider}`,
              }}
            >
              {stage.tasks.map((task, ti) => (
                <React.Fragment key={task.id}>
                  {ti > 0 && <Divider />}
                  <ChecklistTaskRow
                    task={task}
                    checked={!!checked[task.id]}
                    expanded={!!expanded[task.id]}
                    onToggleCheck={() => onCheck(task.id)}
                    onToggleExpand={() => onExpand(task.id)}
                    ageBracket={ageBracket}
                    region={region}
                    registerRef={(el) => registerTaskRef(task.id, el)}
                  />
                </React.Fragment>
              ))}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

export default TrainingPlan;
